import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import {
  ImageOff,
  Filter,
  AlertTriangle,
  Database,
  RotateCcw
} from 'lucide-react';
import { toast } from 'react-toastify';

const AdminMissingImages = () => {
  const [missingImages, setMissingImages] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [tcgFilter, setTcgFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const fetchMissingImages = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        tcg: tcgFilter !== 'all' ? tcgFilter : undefined
      };

      const response = await adminAPI.getMissingImages(params);
      setMissingImages(response.data.data.missingImages);
      setSummary(response.data.data.summary);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total,
        totalPages: response.data.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error fetching missing images:', error);
      toast.error('Failed to fetch missing images report');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, tcgFilter]);

  useEffect(() => {
    fetchMissingImages();
  }, [fetchMissingImages]);

  const handleRefresh = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchMissingImages();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleTcgFilterChange = (newFilter) => {
    setTcgFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getIssueTypeColor = (issueType) => {
    switch (issueType) {
      case 'No CDN URLs':
        return 'bg-red-100 text-red-800';
      case 'Missing image CDN URL':
      case 'Missing small image CDN URL':
        return 'bg-orange-100 text-orange-800';
      case 'Missing thumbnail CDN URL':
      case 'Missing large image CDN URL':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalMissingCount = () => {
    return Object.values(summary).reduce((total, tcg) => total + (tcg.missingImages || 0), 0);
  };

  const getTotalCardsCount = () => {
    return Object.values(summary).reduce((total, tcg) => total + (tcg.totalCards || 0), 0);
  };

  if (loading && missingImages.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ImageOff className="w-8 h-8 mr-3 text-orange-600" />
              Missing Images Report
            </h1>
            <p className="text-gray-600 mt-2">
              Cards missing CDN image URLs across all TCG databases
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="btn btn-md btn-secondary flex items-center"
            >
              <RotateCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <ImageOff className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Missing</p>
              <p className="text-2xl font-bold text-gray-900">{getTotalMissingCount()}</p>
              <p className="text-xs text-gray-500">Cards with missing images</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Cards</p>
              <p className="text-2xl font-bold text-gray-900">{getTotalCardsCount()}</p>
              <p className="text-xs text-gray-500">Across all databases</p>
            </div>
          </div>
        </div>

        {Object.entries(summary).map(([tcgName, stats]) => (
          <div key={tcgName} className="card p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <AlertTriangle className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 capitalize">{tcgName}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.missingImages || 0}</p>
                <p className="text-xs text-gray-500">
                  {stats.percentage}% of {stats.totalCards} cards
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">TCG:</span>
            <select
              value={tcgFilter}
              onChange={(e) => handleTcgFilterChange(e.target.value)}
              className="rounded border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="all">All TCGs</option>
              <option value="pokemon">Pokemon</option>
              <option value="lorcana">Lorcana</option>
            </select>
          </div>
        </div>
      </div>

      {/* Missing Images Table */}
      <div className="card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Missing Images</h3>
            <span className="text-sm text-gray-600">
              {pagination.total} total results
            </span>
          </div>

          {missingImages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Card
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      TCG
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Set
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Issue Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {missingImages.map((card, index) => (
                    <tr key={`${card.tcgType}-${card.cardId}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {card.cardName}
                          </div>
                          <div className="text-sm text-gray-500">
                            #{card.cardNumber} • {card.rarity}
                          </div>
                          <div className="text-xs text-gray-400">
                            ID: {card.cardId}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {card.tcgType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {card.setId || 'Unknown Set'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getIssueTypeColor(card.issueType)}`}>
                          {card.issueType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {card.createdAt ? formatDate(card.createdAt) : 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {card.lastUpdated ? formatDate(card.lastUpdated) : 'Never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <ImageOff className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Missing Images Found</h3>
              <p className="text-gray-600">
                {tcgFilter === 'all'
                  ? 'All cards have proper image URLs configured.'
                  : `All ${tcgFilter} cards have proper image URLs configured.`
                }
              </p>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn btn-sm btn-secondary"
                >
                  Previous
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, pagination.page - 2) + i;
                  if (pageNum > pagination.totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`btn btn-sm ${
                        pageNum === pagination.page
                          ? 'btn-primary'
                          : 'btn-secondary'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="btn btn-sm btn-secondary"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-8 card p-6 bg-blue-50">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">About Missing Images</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>This report shows cards that are missing CDN image URLs. Common issues include:</p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li><strong>No CDN URLs:</strong> Card has no image URLs configured</li>
                <li><strong>Missing specific URLs:</strong> Card is missing either the main image or thumbnail URL</li>
                <li><strong>Empty URLs:</strong> Card has empty or null image URL fields</li>
              </ul>
              <p className="mt-2">
                To fix these issues, run the crawler services which should populate the missing CDN URLs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMissingImages;